import { useState } from 'react';
import { Configuration, SuppliersApi } from '@/api';
import useFindRetailersQuery from '@/api/queries/useFindRetailersQuery';
import Button from '@/components/Button';
import Confirm from '@/components/Confirm';
import CustomToast from '@/components/CustomToast';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import Pagination from '@/components/Pagination';
import { SearchProps } from '@/components/search/useSearch';
import { Supplier } from '@/models/Supplier';
import { SupplierToRetailerEditPath } from '@/modules/admin/paths';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';

interface SupplierToRetailerTableProps extends SearchProps {
  supplier: Supplier;
}

const SupplierToRetailerTable = ({
  supplier,
  searchQuery,
  page = 1,
  onChangePage = () => {},
}: SupplierToRetailerTableProps) => {
  const suppliersApi = new SuppliersApi(new Configuration(useAuth));

  const { retailers, isLoading, invalidateQuery } = useFindRetailersQuery(
    page,
    searchQuery,
    supplier.id,
  );

  const [deletingRetailerId, setDeletingRetailerId] = useState<string | null>(null);

  const handleDeleteRetailer = async (retailer: { id: string; name: string }) => {
    setDeletingRetailerId(retailer.id);
    try {
      await suppliersApi.removeRetailersFromSupplier(supplier.id, [{ id: retailer.id }]);

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={`Retailer "${retailer.name}" removed successfully!`}
          visible={t.visible}
        />
      ));

      await invalidateQuery();
    } catch (error) {
      console.error('Failed to remove retailer from supplier:', error);
      toast.error('Failed to remove retailer.');
    } finally {
      setDeletingRetailerId(null);
    }
  };

  return (
    <div className="table-container">
      <div className="table-wrapper">
        <table className={isLoading ? 'loading' : ''}>
          <thead>
            <tr>
              <th>Retailer</th>
              <th>Customer Number</th>
              <th>Supplier</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!isLoading && retailers.items.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center py-6 text-gray-500">
                  <div className="flex flex-col items-center justify-center space-y-2">
                    <MagnifyingGlassIcon className="h-8 w-8 text-gray-400" aria-hidden="true" />
                    <span>No retailers found</span>
                  </div>
                </td>
              </tr>
            ) : (
              retailers.items.map((retailer) => {
                if (!retailer.id) return null;
                return (
                  <tr key={retailer.id}>
                    <td>
                      <Link
                        to={SupplierToRetailerEditPath.withParams(supplier.id, retailer.id)}
                        className="text-blue-500 hover:underline"
                      >
                        {retailer.name}
                      </Link>
                    </td>
                    <td>{retailer.customerCode}</td>
                    <td>{supplier.name}</td>
                    <td>
                      <Confirm
                        onConfirm={() => handleDeleteRetailer(retailer)}
                        title="Delete Retailer"
                        message={`Are you sure you want to remove the link between supplier "${supplier.name}" and retailer "${retailer.name}"?`}
                        danger
                      >
                        {(handleClick, loading) => (
                          <Button
                            onClick={handleClick}
                            icon={<XMarkIcon className="h-5 w-5 mr-1" aria-hidden="true" />}
                            loading={loading || deletingRetailerId === retailer.id}
                            variant="delete"
                            size="xs"
                          >
                            Delete
                          </Button>
                        )}
                      </Confirm>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        {isLoading && (
          <div role="status" className="loader">
            <SpinnerIcon />
            <span className="sr-only">Loading...</span>
          </div>
        )}
      </div>
      <div className="table-bottom">
        <Pagination
          currentPage={page}
          itemsPerPage={10}
          totalCount={retailers.totalCount || 0}
          estimatedTotalCount={retailers.estimatedTotalCount || 0}
          onClick={onChangePage}
        />
      </div>
    </div>
  );
};

export default SupplierToRetailerTable;
